// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { sparseItemSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { integrationSchema } from '@/utilities/integration';
import { z } from 'zod';

/**
 * Triage item status filter options.
 */
export type ItemTriageStatus = 'all' | 'NEEDS_REVIEW' | 'MATCHED' | 'DISMISSED';

/**
 * Schema for an item triage entry from the API.
 */
export const itemTriageSchema = z.object({
  id: z.string(),
  key: z.string(),
  externalSystem: integrationSchema,
  externalId: z.string(),
  itemName: z.string(),
  sku: z.string().nullable(),
  createdAt: dateTimeSchema,
  triagedAt: dateTimeSchema.nullable(),
  isTriaged: z.boolean(),
  isDismissed: z.boolean(),
  dismissedAt: dateTimeSchema.nullable(),
  status: z.string(),
  item: sparseItemSchema.nullable(),
});

export type ItemTriage = z.infer<typeof itemTriageSchema>;

/**
 * Classification type for triaging items.
 */
const classificationTypeSchema = z.enum([
  'NON_PHYSICAL_PRODUCT',
  'SERIALIZED_PRODUCT',
  'BATCH_PART',
]);

export type ClassificationType = z.infer<typeof classificationTypeSchema>;

/**
 * Request schema for classifying a triage item.
 */
export const classifyItemRequestSchema = z.object({
  classification: classificationTypeSchema.optional(),
  name: z.string().optional(),
  sku: z.string().optional(),
  existingItemId: z.string().optional(),
});

export type ClassifyItemRequest = z.infer<typeof classifyItemRequestSchema>;

/**
 * Filter parameters for listing triage items.
 */
export interface ItemTriageFilter {
  status?: ItemTriageStatus;
  page?: number;
  limit?: number;
}

/**
 * Schema for triage item counts by status.
 */
const triageItemCountsSchema = z.object({
  needsReview: z.number(),
  matched: z.number(),
});

export type TriageItemCounts = z.infer<typeof triageItemCountsSchema>;

/**
 * Schema for triage item counts API response.
 */
export const triageItemCountsResponseSchema = z.object({
  data: triageItemCountsSchema,
});
